// =============================================================================
// FILE: packages/renderer/src/lib/rackDrafterGenerator.ts
// PURPOSE: "On-Demand Rack Drafter" — builds a professional SVG rack elevation
//   (keynotes, symbols, general notes, title block) from a selected equipment
//   list, and assembles a scalable telecom drawing package PDF. The sheet
//   categories are fixed; actual sheet count expands by floors, rooms, racks,
//   risers, buildings, and project scope.
// =============================================================================

import { jsPDF } from 'jspdf';

const PAGE_W = 1754;
const PAGE_H = 1240;
const RACK_U = 42;

export interface LibraryItem { id: string; label: string; ru: number; cat: string; color: string }

export const RACK_LIBRARY: LibraryItem[] = [
  { id: 'odf',      label: 'Fiber Entrance / ODF',       ru: 2, cat: 'Fiber',      color: '#818CF8' },
  { id: 'panel48',  label: '48-Port Patch Panel',        ru: 1, cat: 'Copper',     color: '#38BDF8' },
  { id: 'panel24',  label: '24-Port Patch Panel',        ru: 1, cat: 'Copper',     color: '#38BDF8' },
  { id: 'mgr',      label: 'Horizontal Cable Manager',   ru: 1, cat: 'Management',color: '#64748B' },
  { id: 'switch48', label: '48-Port PoE+ Switch',        ru: 1, cat: 'Active',     color: '#2DD4BF' },
  { id: 'switch24', label: '24-Port Switch',             ru: 1, cat: 'Active',     color: '#2DD4BF' },
  { id: 'ups',      label: 'UPS / Power Module',         ru: 2, cat: 'Power',      color: '#F59E0B' },
  { id: 'server',   label: '2U Application Server',      ru: 2, cat: 'Compute',    color: '#34D399' },
  { id: 'kvm',      label: 'KVM / Console Drawer',       ru: 1, cat: 'Management',color: '#64748B' },
  { id: 'blank',    label: 'Blanking Panel',             ru: 1, cat: 'Filler',     color: '#3F3F46' },
];

export interface Selection { item: LibraryItem; qty: number }
export interface TitleBlock {
  projectName: string; client: string; drawnBy: string;
  drawingNo: string; logoDataUrl?: string;
}
export interface ProjectScope {
  buildings: number;
  floors: number;
  telecomRooms: number;
  racks: number;
  riserZones: number;
  detailSheets: number;
}
export const DEFAULT_PROJECT_SCOPE: ProjectScope = { buildings: 1, floors: 3, telecomRooms: 2, racks: 1, riserZones: 1, detailSheets: 0 };
function count(n: number, min = 1, max = 30): number { return Math.max(min, Math.min(max, Math.floor(Number.isFinite(n) ? n : min))); }
function cleanScope(scope?: Partial<ProjectScope>): ProjectScope {
  return {
    buildings: count(scope?.buildings ?? DEFAULT_PROJECT_SCOPE.buildings, 1, 10),
    floors: count(scope?.floors ?? DEFAULT_PROJECT_SCOPE.floors, 1, 80),
    telecomRooms: count(scope?.telecomRooms ?? DEFAULT_PROJECT_SCOPE.telecomRooms, 1, 80),
    racks: count(scope?.racks ?? DEFAULT_PROJECT_SCOPE.racks, 1, 120),
    riserZones: count(scope?.riserZones ?? DEFAULT_PROJECT_SCOPE.riserZones, 1, 30),
    detailSheets: count(scope?.detailSheets ?? DEFAULT_PROJECT_SCOPE.detailSheets, 0, 30),
  };
}
export function estimateRackPackageSheetCount(scope?: Partial<ProjectScope>): number {
  const s = cleanScope(scope);
  const floorPlanSheets = Math.max(1, Math.ceil((s.buildings * s.floors) / 4));
  const roomSheets = Math.max(1, Math.ceil(s.telecomRooms / 4));
  return 1 + floorPlanSheets + roomSheets + s.riserZones + s.racks + 1 + s.detailSheets;
}

export interface PlacedUnit { label: string; ru: number; color: string; keynote: number; startU: number }

/** Stack selections from the bottom of a 42U rack; returns placed units + BOM. */
export function placeUnits(selections: Selection[]): { placed: PlacedUnit[]; keynotes: Array<{ n: number; label: string; totalRu: number; qty: number }> } {
  const placed: PlacedUnit[] = [];
  const keyMap = new Map<string, number>();
  const bom = new Map<number, { label: string; totalRu: number; qty: number }>();
  let cursor = 1;
  let keynote = 0;
  for (const sel of selections) {
    if (!keyMap.has(sel.item.id)) { keynote += 1; keyMap.set(sel.item.id, keynote); }
    const kn = keyMap.get(sel.item.id)!;
    for (let i = 0; i < sel.qty; i++) {
      if (cursor + sel.item.ru - 1 > RACK_U) break;
      placed.push({ label: sel.item.label, ru: sel.item.ru, color: sel.item.color, keynote: kn, startU: cursor });
      cursor += sel.item.ru;
    }
    const totalUnits = placed.filter((p) => p.keynote === kn).length;
    bom.set(kn, { label: sel.item.label, totalRu: sel.item.ru, qty: totalUnits });
  }
  const keynotes = Array.from(bom.entries()).map(([n, v]) => ({ n, ...v })).sort((a, b) => a.n - b.n);
  return { placed, keynotes };
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function bulletLines(items: string[], x: number, y: number, gap = 34): string {
  return items.map((item, i) => `<text x="${x}" y="${y + i * gap}" font-family="Inter, sans-serif" font-size="18" fill="rgba(200,214,235,0.78)">• ${esc(item)}</text>`).join('');
}

function coverSvg(tb: TitleBlock, scope: ProjectScope): string {
  const totalSheets = estimateRackPackageSheetCount(scope);
  const items = [
    'Minimum drawing categories: T0.0 Cover/Notes, T1.x Floor Plans, T2.x TR/ER Layouts, T3.x Backbone Riser, T4.x Rack Elevations, T5.x Schedules.',
    'Actual sheet count is dynamic — expands for multiple floors, buildings, telecom rooms, racks, riser zones, and scope complexity.',
    'T1.x floor plans repeat per level, area, matchline zone, building wing, or enlarged plan where required.',
    'T2.x room layouts repeat per MDF, IDF, ER, TR, or dense equipment room; T4.x rack elevations repeat per rack or rack group.',
    'T5.x schedules aggregate all floors, rooms, device IDs, cable IDs, destinations, ports, counts, and terminations.',
    `Current input-driven package: ${scope.buildings} building(s), ${scope.floors} floor(s), ${scope.telecomRooms} telecom room(s), ${scope.racks} rack(s), ${scope.riserZones} riser zone(s), ${scope.detailSheets} detail sheet(s) → ${totalSheets} generated sheets.`,
  ];
  const inner = `
    <text x="140" y="110" font-family="Inter, sans-serif" font-weight="900" font-size="44" fill="#F0F5FF">Telecom Drawing Package</text>
    <text x="142" y="150" font-family="Inter, sans-serif" font-size="18" fill="#5EEAD4" letter-spacing="3">SCALABLE LOW VOLTAGE / STRUCTURED CABLING SET</text>
    <text x="140" y="230" font-family="Inter, sans-serif" font-size="30" font-weight="800" fill="#F0F5FF">${esc(tb.projectName || 'Untitled Project')}</text>
    <text x="140" y="270" font-family="Inter, sans-serif" font-size="20" fill="rgba(200,214,235,0.78)">Client · ${esc(tb.client || '—')}  |  Drawing seed · ${esc(tb.drawingNo || 'R2-T-000')}</text>
    <rect x="140" y="330" width="1460" height="470" rx="14" fill="rgba(8,14,26,0.72)" stroke="rgba(37,181,168,0.28)" stroke-width="1.6" />
    <text x="175" y="390" font-family="Inter, sans-serif" font-size="24" font-weight="800" letter-spacing="2" fill="rgba(148,172,210,0.82)">PACKAGE BASIS</text>
    ${bulletLines(items, 175, 440)}
    <rect x="140" y="850" width="1460" height="125" rx="14" fill="rgba(37,181,168,0.06)" stroke="rgba(37,181,168,0.24)" />
    <text x="175" y="900" font-family="Inter, sans-serif" font-size="26" font-weight="900" fill="#5EEAD4">R2 Intelligence Chain</text>
    <text x="175" y="943" font-family="Inter, sans-serif" font-size="20" fill="rgba(226,232,240,0.82)">PDF/DWG ingestion → device extraction → cable intelligence → rack automation → riser generation → QA/QC → deliverable export</text>
  `;
  return frame(inner, 'T0.0 · COVER SHEET / TELECOM GENERAL NOTES');
}

function floorPlansSvg(_tb: TitleBlock, scope: ProjectScope, pageIndex: number): string {
  const allLevels = Array.from({ length: scope.buildings * scope.floors }, (_, i) => {
    const building = Math.floor(i / scope.floors) + 1;
    const floor = (i % scope.floors) + 1;
    return `Building ${building} · Level ${String(floor).padStart(2, '0')}`;
  });
  const levels = allLevels.slice(pageIndex * 4, pageIndex * 4 + 4);
  const blocks = levels.map((level, i) => {
    const x = 145 + (i % 2) * 710;
    const y = 220 + Math.floor(i / 2) * 355;
    return `<g>
      <rect x="${x}" y="${y}" width="620" height="250" rx="10" fill="rgba(8,14,26,0.68)" stroke="rgba(37,181,168,0.24)" />
      <text x="${x + 22}" y="${y + 42}" font-family="Inter" font-size="22" font-weight="800" fill="#F0F5FF">${esc(level)}</text>
      <path d="M ${x + 30} ${y + 85} H ${x + 570} V ${y + 210} H ${x + 30} Z M ${x + 210} ${y + 85} V ${y + 210} M ${x + 390} ${y + 85} V ${y + 210} M ${x + 30} ${y + 145} H ${x + 570}" fill="none" stroke="rgba(148,172,210,0.45)" stroke-width="2" />
      ${[0,1,2,3,4,5].map((n) => `<circle cx="${x + 85 + n * 78}" cy="${y + 117 + (n % 2) * 58}" r="8" fill="#38BDF8"/><text x="${x + 100 + n * 78}" y="${y + 122 + (n % 2) * 58}" font-family="Inter" font-size="13" fill="rgba(226,232,240,0.82)">D${i + 1}-${n + 1}</text>`).join('')}
      <path d="M ${x + 70} ${y + 205} C ${x + 210} ${y + 160}, ${x + 365} ${y + 235}, ${x + 540} ${y + 125}" fill="none" stroke="#F59E0B" stroke-width="3" stroke-dasharray="8 8" />
    </g>`;
  }).join('');
  const inner = `
    <text x="140" y="90" font-family="Inter" font-weight="900" font-size="40" fill="#F0F5FF">Telecom Floor Plans</text>
    <text x="142" y="122" font-family="Inter" font-size="18" fill="#5EEAD4" letter-spacing="3">T1.${pageIndex + 1} · MULTI-FLOOR / MULTI-AREA DEVICE + PATHWAY PLANS</text>
    <text x="140" y="168" font-family="Inter" font-size="18" fill="rgba(200,214,235,0.78)">Generated sheets repeat for every floor, building, zone, enlarged plan, or matchline area required by project scope.</text>
    ${blocks}
  `;
  return frame(inner, `T1.${pageIndex + 1} · TELECOM FLOOR PLANS · ${allLevels.length} LEVEL / BUILDING AREA(S)`);
}

function telecomRoomsSvg(_tb: TitleBlock, scope: ProjectScope, pageIndex: number): string {
  const allRooms = Array.from({ length: scope.telecomRooms }, (_, i) => i === 0 ? 'MDF / Entrance Facility' : `IDF / TR-${String(i + 1).padStart(2, '0')}`);
  const rooms = allRooms.slice(pageIndex * 4, pageIndex * 4 + 4);
  const blocks = rooms.map((room, i) => {
    const x = 150 + (i % 2) * 705;
    const y = 220 + Math.floor(i / 2) * 355;
    return `<g>
      <rect x="${x}" y="${y}" width="610" height="250" rx="10" fill="rgba(8,14,26,0.72)" stroke="rgba(37,181,168,0.24)" />
      <text x="${x + 22}" y="${y + 42}" font-family="Inter" font-size="22" font-weight="800" fill="#F0F5FF">${esc(room)}</text>
      <rect x="${x + 55}" y="${y + 86}" width="82" height="118" fill="rgba(45,212,191,0.20)" stroke="#2DD4BF"/><text x="${x + 96}" y="${y + 224}" text-anchor="middle" font-family="Inter" font-size="13" fill="#2DD4BF">RACK</text>
      <rect x="${x + 165}" y="${y + 86}" width="82" height="118" fill="rgba(56,189,248,0.18)" stroke="#38BDF8"/><text x="${x + 206}" y="${y + 224}" text-anchor="middle" font-family="Inter" font-size="13" fill="#38BDF8">CAB</text>
      <path d="M ${x + 60} ${y + 65} H ${x + 525} V ${y + 192}" fill="none" stroke="#F59E0B" stroke-width="4" stroke-dasharray="10 7"/>
      <text x="${x + 310}" y="${y + 118}" font-family="Inter" font-size="15" fill="rgba(226,232,240,0.76)">Cable tray route</text>
      <text x="${x + 310}" y="${y + 152}" font-family="Inter" font-size="15" fill="rgba(226,232,240,0.76)">Power / grounding zone</text>
      <text x="${x + 310}" y="${y + 186}" font-family="Inter" font-size="15" fill="rgba(226,232,240,0.76)">Clearance + cooling notes</text>
    </g>`;
  }).join('');
  const inner = `
    <text x="140" y="90" font-family="Inter" font-weight="900" font-size="40" fill="#F0F5FF">Telecom Room Layouts</text>
    <text x="142" y="122" font-family="Inter" font-size="18" fill="#5EEAD4" letter-spacing="3">T2.${pageIndex + 1} · MDF / IDF / TR ROOM SERIES</text>
    <text x="140" y="168" font-family="Inter" font-size="18" fill="rgba(200,214,235,0.78)">One layout sheet can be generated per telecom room, with enlarged details for dense or critical rooms.</text>
    ${blocks}
  `;
  return frame(inner, `T2.${pageIndex + 1} · TELECOM ROOM LAYOUTS · ${allRooms.length} ROOM(S)`);
}

function riserSvg(_tb: TitleBlock, scope: ProjectScope, zoneIndex: number): string {
  const floors = Array.from({ length: Math.min(scope.floors, 8) }, (_, i) => `LEVEL ${String(scope.floors - i).padStart(2, '0')}${i === scope.floors - 1 ? ' / MDF' : ''}`);
  const rows = floors.map((floor, i) => {
    const y = 220 + i * 140;
    return `<g>
      <text x="145" y="${y + 12}" font-family="Inter" font-size="19" font-weight="800" fill="#F0F5FF">${floor}</text>
      <line x1="300" y1="${y}" x2="1500" y2="${y}" stroke="rgba(148,172,210,0.22)"/>
      <rect x="420" y="${y - 34}" width="145" height="58" rx="8" fill="rgba(45,212,191,0.16)" stroke="#2DD4BF"/><text x="492" y="${y}" text-anchor="middle" font-family="Inter" font-size="15" fill="#2DD4BF">${i === 4 ? 'MDF' : `IDF-${5 - i}`}</text>
      <rect x="760" y="${y - 34}" width="145" height="58" rx="8" fill="rgba(56,189,248,0.14)" stroke="#38BDF8"/><text x="832" y="${y}" text-anchor="middle" font-family="Inter" font-size="15" fill="#38BDF8">Fiber</text>
      <rect x="1100" y="${y - 34}" width="145" height="58" rx="8" fill="rgba(245,158,11,0.14)" stroke="#F59E0B"/><text x="1172" y="${y}" text-anchor="middle" font-family="Inter" font-size="15" fill="#F59E0B">Copper</text>
    </g>`;
  }).join('');
  const inner = `
    <text x="140" y="90" font-family="Inter" font-weight="900" font-size="40" fill="#F0F5FF">Backbone Riser Diagram</text>
    <text x="142" y="122" font-family="Inter" font-size="18" fill="#5EEAD4" letter-spacing="3">T3.${zoneIndex + 1} · MULTI-FLOOR / MULTI-BUILDING BACKBONE · RISER ZONE ${zoneIndex + 1}</text>
    <path d="M 492 205 V 795 M 832 205 V 795 M 1172 205 V 795" stroke="#5EEAD4" stroke-width="4" stroke-dasharray="12 9" fill="none"/>
    ${rows}
    <text x="140" y="925" font-family="Inter" font-size="18" fill="rgba(200,214,235,0.78)">Riser sheets expand for separate shafts, buildings, campuses, redundant paths, or fiber/copper backbone zones.</text>
  `;
  return frame(inner, `T3.${zoneIndex + 1} · BACKBONE RISER DIAGRAM · RISER ZONE ${zoneIndex + 1} OF ${scope.riserZones}`);
}

// ─── Elevation page ───────────────────────────────────────────────────────────

export function buildElevationSvg(sel: Selection[], tb: TitleBlock, rackIndex = 0, rackTotal = 1): string {
  const { placed, keynotes } = placeUnits(sel);
  const rackX = 140, rackY = 150, rackW = 460, uH = 24;
  const rackH = RACK_U * uH;

  const rows = Array.from({ length: RACK_U }, (_, i) => {
    const y = rackY + i * uH;
    return `<line x1="${rackX}" y1="${y}" x2="${rackX + rackW}" y2="${y}" stroke="rgba(40,68,116,0.3)" stroke-width="0.6" />
      <text x="${rackX - 12}" y="${y + uH - 6}" text-anchor="end" font-family="monospace" font-size="12" fill="rgba(100,116,139,0.7)">${RACK_U - i}</text>`;
  }).join('');

  const units = placed.map((p) => {
    const y = rackY + (RACK_U - (p.startU + p.ru - 1)) * uH;
    const h = p.ru * uH;
    return `<g>
      <rect x="${rackX + 4}" y="${y + 2}" width="${rackW - 8}" height="${h - 4}" rx="3" fill="${p.color}22" stroke="${p.color}" stroke-width="1.4" />
      <rect x="${rackX + 4}" y="${y + 2}" width="5" height="${h - 4}" fill="${p.color}" />
      <text x="${rackX + 22}" y="${y + h / 2 + 5}" font-family="Inter, sans-serif" font-size="14" font-weight="600" fill="#E2E8F0">${esc(p.label)}</text>
      <circle cx="${rackX + rackW - 24}" cy="${y + h / 2}" r="10" fill="none" stroke="${p.color}" stroke-width="1.3" />
      <text x="${rackX + rackW - 24}" y="${y + h / 2 + 4}" text-anchor="middle" font-family="Inter, sans-serif" font-size="12" font-weight="700" fill="${p.color}">${p.keynote}</text>
    </g>`;
  }).join('');

  const keynoteList = keynotes.map((k, i) => `
    <text x="720" y="${210 + i * 34}" font-family="Inter, sans-serif" font-size="18" fill="#E2E8F0">
      <tspan font-weight="800" fill="#5EEAD4">[${k.n}]</tspan>  ${esc(k.label)} — ${k.qty}× (${k.totalRu}U)
    </text>`).join('');

  const notes = [
    'All work per ANSI/TIA-568 & BICSI TDMM latest edition.',
    'Maintain 1U blanking in unused positions for airflow.',
    'Bond rack to TMGB per ANSI/TIA-607.',
    'Label all ports per ANSI/TIA-606 identification scheme.',
    'Verify clearances before installation.',
  ].map((n, i) => `<text x="720" y="${640 + i * 30}" font-family="Inter, sans-serif" font-size="16" fill="rgba(160,180,215,0.75)">• ${esc(n)}</text>`).join('');

  const logo = tb.logoDataUrl
    ? `<image href="${tb.logoDataUrl}" x="1240" y="${PAGE_H - 250}" width="120" height="120" preserveAspectRatio="xMidYMid meet" />`
    : `<g transform="translate(1250,${PAGE_H - 240})"><rect x="4" y="0" width="14" height="80" rx="7" fill="#E8FBF8"/><path d="M 18,5 C 64,5 64,46 18,46" fill="none" stroke="#25B5A8" stroke-width="11" stroke-linecap="round"/><text x="42" y="18" font-family="Inter" font-weight="900" font-size="52" fill="#E0FFFE">2</text></g>`;

  const inner = `
    <text x="140" y="90" font-family="Inter, sans-serif" font-weight="800" font-size="40" fill="#F0F5FF">Rack Elevation — Rack ${rackIndex + 1} of ${rackTotal}</text>
    <text x="142" y="122" font-family="Inter, sans-serif" font-size="18" fill="#5EEAD4" letter-spacing="3">T4.${rackIndex + 1} · 42U FRONT VIEW · DYNAMIC RACK SERIES</text>
    <rect x="${rackX - 30}" y="${rackY - 30}" width="${rackW + 60}" height="${rackH + 50}" rx="10" fill="rgba(8,14,26,0.6)" stroke="rgba(37,181,168,0.3)" stroke-width="2" />
    ${rows}
    ${units}
    <text x="720" y="175" font-family="Inter, sans-serif" font-size="22" font-weight="700" letter-spacing="2" fill="rgba(148,172,210,0.8)">KEYNOTES</text>
    ${keynoteList}
    <text x="720" y="610" font-family="Inter, sans-serif" font-size="22" font-weight="700" letter-spacing="2" fill="rgba(148,172,210,0.8)">GENERAL NOTES</text>
    ${notes}

    <rect x="1220" y="${PAGE_H - 260}" width="450" height="200" rx="8" fill="rgba(8,14,26,0.8)" stroke="rgba(37,181,168,0.35)" stroke-width="1.5" />
    ${logo}
    <text x="1240" y="${PAGE_H - 200}" font-family="Inter, sans-serif" font-size="22" font-weight="800" fill="#F0F5FF">${esc(tb.projectName || 'Untitled Project')}</text>
    <text x="1240" y="${PAGE_H - 172}" font-family="Inter, sans-serif" font-size="16" fill="rgba(200,214,235,0.75)">Client · ${esc(tb.client || '—')}</text>
    <text x="1240" y="${PAGE_H - 146}" font-family="Inter, sans-serif" font-size="16" fill="rgba(200,214,235,0.75)">Drawn by · ${esc(tb.drawnBy || '—')}</text>
    <text x="1240" y="${PAGE_H - 120}" font-family="Inter, sans-serif" font-size="16" fill="rgba(200,214,235,0.75)">Drawing · ${esc(tb.drawingNo || 'RCD2-RE-001')}</text>
    <text x="1240" y="${PAGE_H - 90}" font-family="Inter, sans-serif" font-size="13" fill="#5EEAD4" letter-spacing="1">CERTIFIED BY RCD²</text>
  `;
  return frame(inner, `T4.${rackIndex + 1} · RACK ELEVATION · RACK ${rackIndex + 1} OF ${rackTotal}`);
}

function detailsSvg(scope: ProjectScope, pageIndex: number): string {
  const detailItems = ['Sleeve / penetration detail', 'Cable tray support detail', 'Grounding / bonding detail', 'Firestop / pathway seal detail', 'Wall outlet mounting detail', 'Rack grounding detail'];
  const inner = `
    <text x="140" y="90" font-family="Inter" font-weight="900" font-size="40" fill="#F0F5FF">Telecom Installation Details</text>
    <text x="142" y="122" font-family="Inter" font-size="18" fill="#5EEAD4" letter-spacing="3">T6.${pageIndex + 1} · PATHWAYS / SLEEVES / GROUNDING / INSTALLATION DETAILS</text>
    ${detailItems.map((item, i) => {
      const x = 145 + (i % 2) * 710;
      const y = 210 + Math.floor(i / 2) * 245;
      return `<g><rect x="${x}" y="${y}" width="620" height="180" rx="10" fill="rgba(8,14,26,0.68)" stroke="rgba(37,181,168,0.24)"/><text x="${x + 24}" y="${y + 42}" font-family="Inter" font-size="21" font-weight="800" fill="#F0F5FF">${esc(item)}</text><path d="M ${x + 45} ${y + 100} H ${x + 560} M ${x + 140} ${y + 72} V ${y + 148} M ${x + 265} ${y + 72} V ${y + 148} M ${x + 390} ${y + 72} V ${y + 148}" stroke="#5EEAD4" stroke-width="3" stroke-dasharray="8 8"/><text x="${x + 24}" y="${y + 150}" font-family="Inter" font-size="14" fill="rgba(226,232,240,0.72)">Generated detail placeholder · scope sheet ${pageIndex + 1} of ${scope.detailSheets}</text></g>`;
    }).join('')}
  `;
  return frame(inner, `T6.${pageIndex + 1} · TELECOM DETAILS · SHEET ${pageIndex + 1} OF ${scope.detailSheets}`);
}

function bomSvg(sel: Selection[], tb: TitleBlock): string {
  const { keynotes } = placeUnits(sel);
  const top = 240, rowH = 46;
  const header = `
    <text x="140" y="${top - 18}" font-family="Inter" font-size="20" font-weight="700" letter-spacing="2" fill="rgba(148,172,210,0.7)">KEY</text>
    <text x="260" y="${top - 18}" font-family="Inter" font-size="20" font-weight="700" letter-spacing="2" fill="rgba(148,172,210,0.7)">DESCRIPTION</text>
    <text x="1000" y="${top - 18}" font-family="Inter" font-size="20" font-weight="700" letter-spacing="2" fill="rgba(148,172,210,0.7)">QTY</text>
    <text x="1200" y="${top - 18}" font-family="Inter" font-size="20" font-weight="700" letter-spacing="2" fill="rgba(148,172,210,0.7)">RU EACH</text>
    <text x="1500" y="${top - 18}" text-anchor="end" font-family="Inter" font-size="20" font-weight="700" letter-spacing="2" fill="rgba(148,172,210,0.7)">TOTAL RU</text>
    <rect x="140" y="${top - 6}" width="1360" height="1.5" fill="rgba(37,181,168,0.35)" />`;
  const rows = keynotes.map((k, i) => {
    const y = top + 30 + i * rowH;
    return `
      <text x="140" y="${y}" font-family="Inter" font-size="22" font-weight="800" fill="#5EEAD4">[${k.n}]</text>
      <text x="260" y="${y}" font-family="Inter" font-size="22" fill="#E2E8F0">${esc(k.label)}</text>
      <text x="1000" y="${y}" font-family="Inter" font-size="22" fill="rgba(200,214,235,0.85)">${k.qty}</text>
      <text x="1200" y="${y}" font-family="Inter" font-size="22" fill="rgba(200,214,235,0.85)">${k.totalRu}U</text>
      <text x="1500" y="${y}" text-anchor="end" font-family="Inter" font-size="22" font-weight="600" fill="#F0F5FF">${k.qty * k.totalRu}U</text>
      <rect x="140" y="${y + 12}" width="1360" height="1" fill="rgba(148,172,210,0.08)" />`;
  }).join('');
  const totalRu = keynotes.reduce((s, k) => s + k.qty * k.totalRu, 0);
  const inner = `
    <text x="140" y="90" font-family="Inter" font-weight="800" font-size="40" fill="#F0F5FF">Cable / Termination / Equipment Schedules</text>
    <text x="142" y="122" font-family="Inter" font-size="18" fill="#5EEAD4" letter-spacing="3">T5.x · AGGREGATED ACROSS ALL FLOORS, ROOMS, RACKS, DEVICES, AND TERMINATIONS</text>
    ${header}${rows}
    <text x="1200" y="${top + 40 + keynotes.length * rowH + 20}" font-family="Inter" font-size="24" font-weight="800" fill="#5EEAD4">RACK FILL</text>
    <text x="1500" y="${top + 40 + keynotes.length * rowH + 20}" text-anchor="end" font-family="Inter" font-size="24" font-weight="800" fill="#5EEAD4">${totalRu}U / ${RACK_U}U</text>
  `;
  return frame(inner, 'T5.x · CABLE / TERMINATION / EQUIPMENT SCHEDULES');
}

function frame(inner: string, footer: string): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${PAGE_W}" height="${PAGE_H}" viewBox="0 0 ${PAGE_W} ${PAGE_H}">
    <rect width="${PAGE_W}" height="${PAGE_H}" fill="#0a0f1a" />
    <rect x="30" y="30" width="${PAGE_W - 60}" height="${PAGE_H - 60}" rx="14" fill="none" stroke="rgba(37,181,168,0.3)" stroke-width="2" />
    <rect x="46" y="46" width="${PAGE_W - 92}" height="${PAGE_H - 92}" rx="10" fill="none" stroke="rgba(37,181,168,0.12)" stroke-width="1" />
    ${inner}
    <text x="70" y="${PAGE_H - 55}" font-family="Inter" font-size="18" fill="rgba(148,172,210,0.6)" letter-spacing="1.5">${footer}</text>
  </svg>`;
}

function rasterize(svg: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = PAGE_W; canvas.height = PAGE_H;
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = '#0a0f1a'; ctx.fillRect(0, 0, PAGE_W, PAGE_H);
      ctx.drawImage(img, 0, 0, PAGE_W, PAGE_H);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

export interface RackPackageResult { blobUrl: string; fileName: string }

export async function generateRackPackage(sel: Selection[], tb: TitleBlock, scopeInput?: Partial<ProjectScope>): Promise<RackPackageResult> {
  const scope = cleanScope(scopeInput);
  const floorPlanSheets = Math.max(1, Math.ceil((scope.buildings * scope.floors) / 4));
  const roomSheets = Math.max(1, Math.ceil(scope.telecomRooms / 4));
  const pages = [coverSvg(tb, scope)];
  for (let i = 0; i < floorPlanSheets; i += 1) pages.push(floorPlansSvg(tb, scope, i));
  for (let i = 0; i < roomSheets; i += 1) pages.push(telecomRoomsSvg(tb, scope, i));
  for (let i = 0; i < scope.riserZones; i += 1) pages.push(riserSvg(tb, scope, i));
  for (let i = 0; i < scope.racks; i += 1) pages.push(buildElevationSvg(sel, tb, i, scope.racks));
  pages.push(bomSvg(sel, tb));
  for (let i = 0; i < scope.detailSheets; i += 1) pages.push(detailsSvg(scope, i));
  const urls: string[] = [];
  for (const svg of pages) urls.push(await rasterize(svg));
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const pw = pdf.internal.pageSize.getWidth();
  const ph = pdf.internal.pageSize.getHeight();
  urls.forEach((u, i) => { if (i > 0) pdf.addPage(); pdf.addImage(u, 'PNG', 0, 0, pw, ph, undefined, 'FAST'); });
  const fileName = `RCD2-Rack-Elevation-${(tb.projectName || 'project').replace(/[^a-z0-9]+/gi, '-')}-${Date.now()}.pdf`;
  return { blobUrl: URL.createObjectURL(pdf.output('blob')), fileName };
}
